import React, { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Confirm, Link, useDataProvider, useNotify,
} from 'react-admin';
import { useMutation, useQuery } from '@tanstack/react-query';

import queryClient from '../../utilities/queryClient';
import { useRegisterBreadcrumb } from '../layout/navbar/BreadcrumbsManager';
import MfaStepUp, { StepUpCode } from '../security/MfaStepUp';
import { MFA_STATUS_ID, MFA_STATUS_QUERY_KEY, MfaStatus } from '../security/SecurityPage';
import stepUpActions from '../security/stepUpActions';
import accountSecurityErrorMessage, { isSsoOnlyAccount } from './accountSecurityErrors';
import PasswordPolicySettings from './PasswordPolicySettings';

const ACCOUNT_SETTINGS_RESOURCE = 'accounts/settings';
const SECURITY_SETTINGS_ID = 'security';
const SECURITY_SETTINGS_QUERY_KEY = [ACCOUNT_SETTINGS_RESOURCE, SECURITY_SETTINGS_ID];

type SecuritySettings = {
  id: string;
  mfaRequired: boolean;
};

type SecuritySettingsUpdate = StepUpCode & {
  mfaRequired: boolean;
};

const AccountSecuritySettings = () => {
  useRegisterBreadcrumb({ title: 'Account security', path: '/access-management/account-security' });

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [confirmingRequire, setConfirmingRequire] = useState(false);
  const [steppingUp, setSteppingUp] = useState(false);

  const {
    data: settings,
    error: settingsError,
    refetch: reloadSettings,
  } = useQuery({
    queryKey: SECURITY_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await dataProvider
        .getOne<SecuritySettings>(ACCOUNT_SETTINGS_RESOURCE, { id: SECURITY_SETTINGS_ID });
      return data;
    },
    retry: (failureCount, error) => !isSsoOnlyAccount(error) && failureCount < 3,
  });

  const { data: mfaStatus } = useQuery({
    queryKey: MFA_STATUS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await dataProvider
        .getOne<MfaStatus>('auth/mfa', { id: MFA_STATUS_ID });
      return data;
    },
  });
  const canStepUp = mfaStatus?.enabled === true;

  const {
    mutate: updateSettingsMutate,
    isPending: updateLoading,
  } = useMutation({
    mutationFn: async (update: SecuritySettingsUpdate): Promise<SecuritySettings> => {
      const { data } = await dataProvider.update<SecuritySettings>(ACCOUNT_SETTINGS_RESOURCE, {
        id: SECURITY_SETTINGS_ID,
        data: update,
        previousData: settings,
      });
      return data;
    },
    onSuccess: (updated) => {
      setConfirmingRequire(false);
      setSteppingUp(false);
      queryClient.setQueryData<SecuritySettings>(SECURITY_SETTINGS_QUERY_KEY, updated);
      notify(
        updated.mfaRequired
          ? 'Multi-factor authentication is now required on this account'
          : 'Multi-factor authentication is no longer required on this account',
        { type: 'info' },
      );
    },
    onError: (error) => {
      setConfirmingRequire(false);
      notify(accountSecurityErrorMessage(error, 'Could not save the account security settings'), { type: 'error' });
    },
  });

  const startRequire = useCallback(() => {
    setConfirmingRequire(true);
  }, [setConfirmingRequire]);

  const cancelRequire = useCallback(() => {
    setConfirmingRequire(false);
  }, [setConfirmingRequire]);

  const confirmRequire = useCallback(() => {
    updateSettingsMutate({ mfaRequired: true });
  }, [updateSettingsMutate]);

  const startLower = useCallback(() => {
    setSteppingUp(true);
  }, [setSteppingUp]);

  const cancelLower = useCallback(() => {
    setSteppingUp(false);
  }, [setSteppingUp]);

  const submitLower = useCallback((stepUpCode: StepUpCode) => {
    updateSettingsMutate({ ...stepUpCode, mfaRequired: false });
  }, [updateSettingsMutate]);

  const ssoOnly = isSsoOnlyAccount(settingsError);

  return (
    <Box maxWidth={760} paddingX={4} paddingY={2} display="flex" flexDirection="column" gap={2}>
      <PasswordPolicySettings />
      <Card>
        <CardContent>
          {steppingUp && (
            <MfaStepUp
              loading={updateLoading}
              action={stepUpActions.LOWER_ACCOUNT_REQUIREMENT}
              onSubmit={submitLower}
              onCancel={cancelLower}
            />
          )}
          {!steppingUp && settings && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Require multi-factor authentication
              </Typography>
              <Alert severity={settings.mfaRequired ? 'success' : 'info'} sx={{ marginBottom: 2 }}>
                {settings.mfaRequired
                  ? 'Every member of this account must use an authenticator app when signing in with a password.'
                  : 'Members choose for themselves whether to use an authenticator app when signing in.'}
              </Alert>
              {settings.mfaRequired ? (
                <Box>
                  <Button variant="outlined" color="error" onClick={startLower} disabled={updateLoading || !canStepUp}>
                    Stop requiring
                  </Button>
                  {mfaStatus && !canStepUp && (
                    <Alert severity="info" sx={{ marginTop: 2 }}>
                      You can&apos;t remove the requirement because you have not set up
                      multi-factor authentication yourself. Removing it needs a code from your
                      authenticator app to confirm it is you.
                      {' '}
                      <Link to="/security">Set up multi-factor authentication</Link>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Button variant="contained" onClick={startRequire} disabled={updateLoading}>
                  Require for all members
                </Button>
              )}
            </Box>
          )}
          {!settings && ssoOnly && (
            <Alert severity="info">
              This account signs in through SSO, so there is no multi-factor authentication
              requirement or password policy to set here. Both are handled by your identity
              provider.
            </Alert>
          )}
          {!settings && settingsError && !ssoOnly && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={() => reloadSettings()}>Retry</Button>}
            >
              {accountSecurityErrorMessage(settingsError, 'Could not load the account security settings')}
            </Alert>
          )}
          {!settings && !settingsError && (
            <CircularProgress size={24} />
          )}
        </CardContent>
      </Card>
      <Confirm
        isOpen={confirmingRequire}
        loading={updateLoading}
        title="Require multi-factor authentication?"
        content={(
          <>
            <Typography variant="body1" gutterBottom>
              Members who have not set up an authenticator app are not locked out. They are taken
              through setting one up the next time they sign in.
            </Typography>
            <Typography variant="body1">
              Members who are signed in now stay signed in. The requirement applies from their
              next sign in.
            </Typography>
          </>
        )}
        confirm="Require"
        onConfirm={confirmRequire}
        onClose={cancelRequire}
      />
    </Box>
  );
};

export default AccountSecuritySettings;
